import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/shared/lib/utils'
import type { ShipmentSkuFileColumn } from '@/shared/hooks/api/packagingShipments'

interface ShipmentSkuColumnMappingDialogProps {
  open: boolean
  columns: ShipmentSkuFileColumn[]
  sampleRows: string[][]
  isPending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (skuColumn: number) => void
}

function columnOptionLabel(
  column: ShipmentSkuFileColumn,
  fallback: string
): string {
  const header = column.header.trim()
  if (header === '') {
    return fallback
  }
  return `${column.index}. ${header}`
}

export function ShipmentSkuColumnMappingDialog({
  open,
  columns,
  sampleRows,
  isPending,
  onOpenChange,
  onConfirm,
}: ShipmentSkuColumnMappingDialogProps) {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const [selectedColumn, setSelectedColumn] = useState('')

  useEffect(() => {
    if (open) {
      setSelectedColumn('')
    }
  }, [open])

  const columnIndex = Number.parseInt(selectedColumn, 10)
  const hasSelection = selectedColumn.length > 0 && !Number.isNaN(columnIndex)
  const showSamples = sampleRows.length > 0
  const columnSelectItems = columns.map((column) => ({
    value: String(column.index),
    label: columnOptionLabel(
      column,
      t('shipmentsPage.skuFile.columnFallback', { index: column.index })
    ),
  }))
  const selectedColumnLabel =
    columnSelectItems.find((item) => item.value === selectedColumn)?.label ??
    null
  let confirmLabel = t('common:actions.continue')
  if (isPending) {
    confirmLabel = t('shipmentsPage.skuFile.reading')
  }
  let selectValue: string | undefined
  if (selectedColumn.length > 0) {
    selectValue = selectedColumn
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden sm:max-w-4xl'>
        <DialogHeader className='shrink-0'>
          <DialogTitle>{t('shipmentsPage.skuFile.mappingTitle')}</DialogTitle>
          <DialogDescription>
            {t('shipmentsPage.skuFile.mappingDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden'>
          <div className='shrink-0 space-y-1'>
            <Label htmlFor='shipment-sku-column'>
              {t('shipmentsPage.skuFile.skuColumn')}
            </Label>
            <Select
              value={selectValue}
              onValueChange={(value) => {
                setSelectedColumn(value == null ? '' : String(value))
              }}
              items={columnSelectItems}
            >
              <SelectTrigger id='shipment-sku-column' className='w-full'>
                <SelectValue
                  placeholder={t('shipmentsPage.skuFile.columnPlaceholder')}
                >
                  {selectedColumnLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {columnSelectItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showSamples && (
            <div className='min-h-0 min-w-0 flex-1 overflow-auto rounded-md border'>
              <Table className='min-w-max'>
                <TableHeader>
                  <TableRow>
                    {columns.map((column) => {
                      const label = columnOptionLabel(
                        column,
                        t('shipmentsPage.skuFile.columnFallback', {
                          index: column.index,
                        })
                      )
                      const isSelected =
                        hasSelection && column.index === columnIndex
                      return (
                        <TableHead
                          key={column.index}
                          className={cn(
                            'max-w-40 truncate',
                            isSelected && 'bg-sky-100 dark:bg-sky-950/50'
                          )}
                          title={label}
                        >
                          {label}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sampleRows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {columns.map((column, cellIndex) => (
                        <TableCell
                          key={column.index}
                          className='max-w-40 truncate'
                          title={row[cellIndex] ?? ''}
                        >
                          {row[cellIndex] ?? ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <DialogFooter className='shrink-0'>
          <Button
            variant='outline'
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t('shipmentsPage.skuFile.cancel')}
          </Button>
          <Button
            disabled={!hasSelection || isPending}
            onClick={() => {
              if (!hasSelection) {
                return
              }
              onConfirm(columnIndex)
            }}
          >
            {isPending && (
              <Loader2 className='me-2 size-4 animate-spin' aria-hidden />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
